'use client';

import { useEffect } from 'react';

/**
 * Pauses infinite decorative animations while they are off-screen, and while the tab is hidden.
 *
 * Why: a real CPU profile of the homepage under a throttled mobile load (4x CPU, slow 4G) spent
 * 1,526ms in style recalculation, 555ms in layout and 651ms in paint. The driver is animations that
 * never stop — the marquee tracks, the hero gradient lines and the spinning glow all run
 * `infinite`, so the style engine keeps working even when the element is nowhere near the viewport.
 *
 * It also has a large second-order cost: Microsoft Clarity re-serializes the DOM whenever inline
 * styles mutate, and burned 2,437ms of main thread doing exactly that (99% of it after load). Fewer
 * mutations while off-screen means less for it to chew on.
 *
 * Deliberately cheap: one observer, ~a dozen targets, default single threshold. Pausing is
 * imperceptible — a marquee that stops advancing while scrolled away simply resumes where it was.
 */
const PAUSE_SELECTOR = '.marquee-track, .gradient-line, .animate-spin-slow, .signal-line';

const AnimationPauser = () => {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>(PAUSE_SELECTOR));
    if (!els.length) return;

    // Empty string clears the inline override and restores whatever the stylesheet says.
    const setPaused = (el: HTMLElement, paused: boolean) => {
      el.style.animationPlayState = paused ? 'paused' : '';
    };

    const offscreen = new WeakSet<HTMLElement>();

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          if (entry.isIntersecting) offscreen.delete(el);
          else offscreen.add(el);
          // While the tab is hidden everything stays paused regardless of position.
          setPaused(el, document.hidden || !entry.isIntersecting);
        }
      },
      // Start a little before the element scrolls in, so it is already moving when it appears.
      { rootMargin: '150px' }
    );
    els.forEach((el) => io.observe(el));

    const onVisibility = () => {
      for (const el of els) setPaused(el, document.hidden || offscreen.has(el));
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      els.forEach((el) => setPaused(el, false));
    };
  }, []);

  return null;
};

export default AnimationPauser;
